import React, { useState, useEffect, useCallback } from "react";
import { useMonitor } from "../../contexts/MonitorContext";
import { Heading } from "../../design/heading";
import { Text } from "../../design/text";
import { Badge } from "../../design/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../design/table";
import { Report, ReportStatus } from "@visitproof/types";
import { ReportService } from "../../services/ReportService";
import moment from "moment";
import {
  DocumentTextIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";

const getStatusBadge = (status: ReportStatus) => {
  switch (status) {
    case "pending":
      return <Badge color="yellow">Pending</Badge>;
    case "approved":
      return <Badge color="green">Approved</Badge>;
    case "completed":
      return <Badge color="blue">Completed</Badge>;
    default:
      return <Badge color="zinc">{status}</Badge>;
  }
};

type TabType = "pending" | "approved" | "all";

/**
 * Supervisor/Monitor reports page - shows their submitted reports
 */
const SupervisorReports: React.FC = () => {
  const { supervisorId, supervisor } = useMonitor();
  const [reports, setReports] = useState<Report[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabType>("pending");

  const loadReports = useCallback(async () => {
    if (!supervisorId || !supervisor?.accountId) return;

    try {
      setLoading(true);
      setError(null);
      const data = await ReportService.getReportsBySupervisorId(
        supervisorId,
        supervisor.accountId
      );

      // Fetch visit data for each report
      const reportsWithVisits = await Promise.all(
        data.map(async (report) => {
          if (report.visitId) {
            try {
              const { getDoc, doc } = await import("firebase/firestore");
              const { db } = await import("../../firebase");
              const visitDocRef = doc(db, "visits", report.visitId);
              const visitSnapshot = await getDoc(visitDocRef);
              if (visitSnapshot.exists()) {
                report.visit = { id: visitSnapshot.id, ...visitSnapshot.data() } as any;
              }
            } catch (error) {
              console.error(`Error fetching visit ${report.visitId}:`, error);
            }
          }
          return report;
        })
      );

      setReports(reportsWithVisits);
    } catch (err) {
      console.error("Error loading reports:", err);
      setError(err instanceof Error ? err.message : "Failed to load reports");
    } finally {
      setLoading(false);
    }
  }, [supervisorId, supervisor?.accountId]);

  useEffect(() => {
    loadReports();
  }, [loadReports]);

  const getReportsForTab = () => {
    switch (activeTab) {
      case "pending":
        return reports.filter((r) => r.status === "pending");
      case "approved":
        return reports.filter((r) => r.status === "approved");
      case "all":
        return reports;
      default:
        return reports;
    }
  };

  const displayedReports = getReportsForTab();
  const pendingCount = reports.filter((r) => r.status === "pending").length;
  const approvedCount = reports.filter((r) => r.status === "approved").length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Text>Loading...</Text>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="flex items-start gap-3 p-4 border border-red-200 rounded-xl bg-red-50 dark:border-red-800 dark:bg-red-900/20">
          <ExclamationTriangleIcon className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
          <Text className="text-red-700 dark:text-red-400">{error}</Text>
        </div>
      </div>
    );
  }

  const tabs: { key: TabType; label: string; count: number }[] = [
    { key: "pending", label: "Pending", count: pendingCount },
    { key: "approved", label: "Approved", count: approvedCount },
    { key: "all", label: "All", count: reports.length },
  ];

  return (
    <div className="p-6">
      <div className="mb-6">
        <Heading>My Reports</Heading>
        <Text className="mt-2 text-zinc-600 dark:text-zinc-400">
          View your visit reports and their approval status
        </Text>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 p-1 mb-6 rounded-lg bg-zinc-100 dark:bg-zinc-800 w-fit">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
              activeTab === tab.key
                ? "bg-white dark:bg-zinc-700 text-zinc-900 dark:text-white shadow-sm"
                : "text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-white"
            }`}
          >
            {tab.label} ({tab.count})
          </button>
        ))}
      </div>

      {/* Reports Table or Empty State */}
      {displayedReports.length === 0 ? (
        <div className="p-8 text-center border rounded-lg border-zinc-200 dark:border-zinc-700">
          <DocumentTextIcon className="w-12 h-12 mx-auto mb-3 text-zinc-300 dark:text-zinc-600" />
          <Text className="font-medium text-zinc-900 dark:text-zinc-100">
            No reports found
          </Text>
          <Text className="mt-1 text-zinc-500">
            {activeTab === "pending"
              ? "You don't have any pending reports"
              : activeTab === "approved"
              ? "You don't have any approved reports yet"
              : "Reports will appear here after visits are completed"}
          </Text>
        </div>
      ) : (
        <Table>
          <TableHead>
            <TableRow>
              <TableHeader>Visit Date</TableHeader>
              <TableHeader>Created</TableHeader>
              <TableHeader>Status</TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {displayedReports.map((report) => {
              const visitDate = report.visit?.startDate?.toDate?.();
              const createdAt = (report as any).createdAt?.toDate?.();

              return (
                <TableRow key={report.id} href={`/report/${report.id}`}>
                  <TableCell className="font-medium">
                    {visitDate
                      ? moment(visitDate).format("ddd, MMM D, YYYY")
                      : "—"}
                  </TableCell>
                  <TableCell>
                    {createdAt ? moment(createdAt).fromNow() : "—"}
                  </TableCell>
                  <TableCell>{getStatusBadge(report.status)}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </div>
  );
};

export default SupervisorReports;
